import { useCallback, useEffect, useState } from 'react';
import { ApiClient } from '@utils/apiClient';
import type {
  ScenarioConfig,
  ScenarioCredential,
  ScenarioIssue,
  ScenarioMeta,
  ScenarioState,
  ScenarioTestRequest,
  ScenarioTestResult,
  ScenarioValidation,
} from '../components/scenario/types';

export type ApiOutcome<T> =
  | { ok: true; data: T }
  | { ok: false; message: string; errors: ScenarioIssue[]; warnings: ScenarioIssue[] };

const readJson = async (response: Response): Promise<Record<string, unknown>> => {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const toOutcome = async <T>(response: Response): Promise<ApiOutcome<T>> => {
  const body = await readJson(response);
  if (response.ok) return { ok: true, data: body as T };
  const errors = Array.isArray(body.errors) ? (body.errors as ScenarioIssue[]) : [];
  const warnings = Array.isArray(body.warnings) ? (body.warnings as ScenarioIssue[]) : [];
  const firstFieldError = Object.entries(body).find(([, value]) => Array.isArray(value));
  const message =
    (typeof body.error === 'string' && body.error) ||
    (typeof body.detail === 'string' && body.detail) ||
    (firstFieldError && !errors.length ? `${firstFieldError[0]}: ${String(firstFieldError[1])}` : '') ||
    `HTTP ${response.status}`;
  return { ok: false, message, errors, warnings };
};

const networkFailure = <T>(error: unknown): ApiOutcome<T> => ({
  ok: false,
  message: error instanceof Error ? error.message : String(error),
  errors: [],
  warnings: [],
});

export const useScenario = (agentId?: string) => {
  const [scenario, setScenario] = useState<ScenarioState | null>(null);
  const [meta, setMeta] = useState<ScenarioMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [stateResponse, metaResponse] = await Promise.all([
        ApiClient.get(`/agents/agents/${agentId}/scenario/`),
        ApiClient.get('/agents/agents/scenario-meta-schema/'),
      ]);
      const stateOutcome = await toOutcome<ScenarioState>(stateResponse);
      const metaOutcome = await toOutcome<ScenarioMeta>(metaResponse);
      if (stateOutcome.ok) setScenario(stateOutcome.data);
      else setLoadError(stateOutcome.message);
      if (metaOutcome.ok) setMeta(metaOutcome.data);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (config: ScenarioConfig, enabled: boolean): Promise<ApiOutcome<ScenarioState>> => {
      try {
        const outcome = await toOutcome<ScenarioState>(
          await ApiClient.put(`/agents/agents/${agentId}/scenario/`, { config, enabled })
        );
        if (outcome.ok) setScenario(outcome.data);
        return outcome;
      } catch (error) {
        return networkFailure(error);
      }
    },
    [agentId]
  );

  const validate = useCallback(
    async (config: unknown): Promise<ApiOutcome<ScenarioValidation>> => {
      try {
        return await toOutcome<ScenarioValidation>(
          await ApiClient.post(`/agents/agents/${agentId}/scenario/validate/`, { config })
        );
      } catch (error) {
        return networkFailure(error);
      }
    },
    [agentId]
  );

  const testTool = useCallback(
    async (request: ScenarioTestRequest): Promise<ApiOutcome<ScenarioTestResult>> => {
      try {
        return await toOutcome<ScenarioTestResult>(
          await ApiClient.post(`/agents/agents/${agentId}/scenario/test-tool/`, request)
        );
      } catch (error) {
        return networkFailure(error);
      }
    },
    [agentId]
  );

  return { scenario, meta, loading, loadError, reload: load, save, validate, testTool };
};

export const useScenarioCredentials = (active: boolean) => {
  const [credentials, setCredentials] = useState<ScenarioCredential[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!active) return;
    try {
      const outcome = await toOutcome<ScenarioCredential[]>(
        await ApiClient.get('/agents/scenario-credentials/')
      );
      if (outcome.ok) {
        setCredentials(outcome.data);
        setError(null);
      } else {
        setError(outcome.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = useCallback(
    async (credential: Omit<ScenarioCredential, 'id'> & { id?: string }) => {
      try {
        const payload = {
          ref: credential.ref,
          header_name: credential.header_name,
          header_value: credential.header_value,
        };
        const response = credential.id
          ? await ApiClient.patch(`/agents/scenario-credentials/${credential.id}/`, payload)
          : await ApiClient.post('/agents/scenario-credentials/', payload);
        const outcome = await toOutcome<ScenarioCredential>(response);
        if (outcome.ok) await load();
        return outcome;
      } catch (err) {
        return networkFailure<ScenarioCredential>(err);
      }
    },
    [load]
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        const response = await ApiClient.delete(`/agents/scenario-credentials/${id}/`);
        if (response.ok) await load();
        return response.ok;
      } catch {
        return false;
      }
    },
    [load]
  );

  return { credentials, error, reload: load, upsert, remove };
};
